import React from "react";
import styled from "styled-components";
import MainLayout from "../layouts/main";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-top: 3rem;
  margin-bottom: 1rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const ContactInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ContactInfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StyledWrapper = styled("div")`
  padding-top: 140px;
  min-height: 100vh;
`;

const StyledContainer = styled("div")`
  margin: 0 auto;
  max-width: 1440px;
  width: 90%;
`;

const Terms: React.FC = () => {
  return (
    <MainLayout bg="/images/register-bg.webp">
      <StyledWrapper>
        <StyledContainer>
          <Container>
            <Title>Terms And Condition</Title>
            <Paragraph>
              Welcome to the Pro Hub (the "Pro Hub"), an application provided by
              The Up Rank Company Limited (collectively referred to as "we" or
              "us" or "our"). These Terms and Conditions (the "Terms") set forth
              conditions relating to the use of the Pro Hub and form a legal
              binding relationship between you and us. Your use of the Pro Hub
              is also subject to our Privacy Policy as well as other applicable
              terms and policies for the Pro Hub.
            </Paragraph>
            <Paragraph>
              Please read the Terms carefully and make sure that you understand
              them before using the Pro Hub. By accessing or using the Pro Hub,
              you agree to the terms and conditions set forth in the Terms and
              you agree to comply with them. If you do not agree to the Terms,
              please do not access or use the Pro Hub. We may amend the Terms
              from time to time. We will use commercially reasonable efforts to
              notify of any material changes to the Terms. Your continued access
              or use of the Pro Hub after the changes constitute your acceptance
              of the amended Terms. If you do not agree to the amended Terms,
              you must stop accessing or using the Pro Hub.
            </Paragraph>
            <SectionTitle>1. General Provisions</SectionTitle>
            <Paragraph>
              The Pro Hub is currently available for you on Android and iOS,
              where you can access and use the Pro Hub via your mobile device.
              We may update the Pro Hub and change the requirements for both
              Android and iOS, and for any additional systems that we decide to
              extend the availability of the Pro Hub, from time to time. You
              will need to download the updates if you want to keep using the
              Pro Hub. We will not warrant that we will always update the Pro
              Hub so that it is relevant to you and/or works with the Android
              and iOS version that you have installed on your device. It is your
              responsibility to ensure that the device you are using is
              compatible with the Pro Hub. However, you will always accept
              updates to the Pro Hub when offered to you.
            </Paragraph>
            <Paragraph>
              When using the Pro Hub, you will be responsible for any such
              charges, including roaming data charges if you use the Pro Hub
              outside of your home territory (i.e. region or country) without
              turning off data roaming. If you are not the bill payer for the
              device on which you are using the Pro Hub, we assume that you have
              received permission from the bill payer for using the Pro Hub. We
              are not responsible for the Pro Hub if it is not working at full
              functionality when you do not have access to Wi-Fi and you have
              used all of your data allowance. As a result, you may be charged
              by your mobile provider for the cost of data for the duration of
              the connection while accessing the Pro Hub, or other third party
              charges.
            </Paragraph>
            <Paragraph>
              We may stop providing the Pro Hub and may terminate use of it at
              any time. In doing so, we will use reasonable efforts to notify
              you of such termination through the Pro Hub or other channels as
              appropriate subject to our determination, and will allow a
              reasonable wind-down period before such termination. Unless we
              inform you otherwise, upon termination, you must stop using the
              Pro Hub, and (if needed) delete it from your device.
            </Paragraph>
            <SectionTitle>2. Your Account</SectionTitle>
            <Paragraph>
              To access or use certain features of the Pro Hub, you must create
              an account with us. When you create an account with us, you must
              provide us with the accurate and complete information. You are
              responsible for maintaining and keeping your information up to
              date. You can edit your account information by accessing the
              "Account Setting" function within the Pro Hub.
            </Paragraph>
            <Paragraph>
              You are responsible for keeping your account information and
              password or any other relevant information confidential and not
              disclosing your account information and password to any third
              party. You are solely responsible for the activities occur under
              your account. If you know or suspect that anyone other than
              yourself knows your account information and password, you must
              immediately change your password. Notwithstanding the foregoing,
              you agree to be bound by all the activities carried out through
              your account.
            </Paragraph>
            <Paragraph>
              We reserve the right to terminate or suspend your account at any
              time with or without prior notice to you, at our sole discretion.
            </Paragraph>
            <SectionTitle>3. Your Use of the Pro Hub</SectionTitle>
            <Paragraph>
              Your access to and use of the Pro Hub is subject to the Terms and
              all applicable laws and regulations. You must not:
            </Paragraph>
            <List>
              <ListItem>
                use the Pro Hub in any way that causes, or may cause, damage to
                the Pro Hub or other users or impairment of the availability or
                accessibility of the Pro Hub;
              </ListItem>
              <ListItem>
                make unauthorized copies, modify, adapt, translate, reverse
                engineer, disassemble, decompile or create any derivative works
                of the Pro Hub or any content therein or determine or attempt to
                determine any source code, algorithms, methods or techniques
                embodied by the Pro Hub;
              </ListItem>
              <ListItem>
                use the Pro Hub in any way which is unlawful, illegal,
                fraudulent or harmful, or in connection with any unlawful,
                illegal, fraudulent or harmful purpose or activity;
              </ListItem>
              <ListItem>
                use the Pro Hub by knowingly introducing viruses, trojans,
                worms, logic bombs or other material which is malicious or
                technologically harmful. You must not attempt to gain
                unauthorised access to the Pro Hub, the server on which the Pro
                Hub is stored or any server, computer or database connected to
                the Pro Hub;
              </ListItem>
              <ListItem>
                conduct any systematic or automated data collection activities
                (including without limitation scraping, data mining, data
                extraction and data harvesting) on or in relation to the Pro Hub
                without our prior express written consent;
              </ListItem>
              <ListItem>
                impersonate any person or entity, or falsely state or otherwise
                misrepresent yourself with any person or entity;
              </ListItem>
              <ListItem>
                use the Pro Hub to transmit or send unsolicited commercial
                communications.
              </ListItem>
            </List>
            <SectionTitle>4. Your Privacy Rights</SectionTitle>
            <Paragraph>
              Please review our Privacy Policy to understand how we collect,
              use, and disclose your personal data regarding the use of the Pro
              Hub.
            </Paragraph>
            <SectionTitle>5. What is the Pro Hub?</SectionTitle>
            <Paragraph>
              The Pro Hub is an application which allows you to:
            </Paragraph>
            <List>
              <ListItem>
                Browse the entertainment related content and obtain the latest
                entertainment information in relation to the movie entitled "The
                Pro Hub";
              </ListItem>
              <ListItem>
                Purchase the electronic vouchers which are available on the Pro
                Hub (the "Coin"). Please refer to 6. How to Use the Coin? to
                learn more about the details of the Coin.
              </ListItem>
            </List>
            <SectionTitle>6. How to Use the Coin?</SectionTitle>
            <List>
              <ListItem>
                You may purchase the Coin within the Pro Hub by using the
                authorized payment methods.
              </ListItem>
              <ListItem>
                The Price of the Coin or the relevant bundle will be displayed
                at the point of purchase in the Pro Hub. You will be responsible
                for the payment of any Coin purchased by you. Once your purchase
                has been completed, your purchased Coin will be available in
                your wallet.
              </ListItem>
              <ListItem>
                After purchasing the Coin, you will be entitled to use or redeem
                the Coin on the Pro Hub for benefits and privileges in
                accordance with the Coin or the bundle you purchased, subject to
                the terms and conditions of each Coin. We reserve the right to
                change the terms and conditions of the Coin at any time without
                prior notice
              </ListItem>
              <ListItem>
                You can see the available Coin and the used or expired Coin by
                checking your wallet.
              </ListItem>
              <ListItem>
                Each Coin has specific terms and conditions which will be
                presented to you on the Pro Hub. Please carefully check such
                specific terms and conditions to ensure you fully understand the
                terms and conditions for using each Coin.
              </ListItem>
              <ListItem>
                The Coin is valid up to the expiry date as prescribed in the
                terms and conditions of each Coin. You must redeem or use the
                Coin within the prescribed period. If the Coin is not used
                within the prescribed period, the Coin will be expired and can
                no longer be used or redeemed and may not be changed into cash
                or any other remuneration.
              </ListItem>
              <ListItem>
                After the Coin is used or redeemed in accordance with the
                relevant terms and conditions, such Coin may not be transferred,
                reused or used for any other purposes.
              </ListItem>
              <ListItem>
                The available Coin is transferable. You may transfer the
                available Coin to other user who has an active account with us.
              </ListItem>
              <ListItem>
                he Coin cannot be cancelled, exchanged, changed into cash,
                compensated, or exchanged with any other thing, unless otherwise
                specified by us on a case by case basis.
              </ListItem>
              <ListItem>
                The Coin will expire automatically upon termination of user's
                account for any reason.
              </ListItem>
              <ListItem>
                The Coin offered by third party (the "Third Party Coin") may
                also be available on the Pro Hub. We suggest that you carefully
                read the prescribed terms and conditions of each Third Party
                Coin. We shall not be responsible for any loss or damage
                suffered by you as a result of the use or redeem of such Third
                Party Coin which is not a result of our fault.
              </ListItem>
              <ListItem>
                We, our affiliates, officers, employees, directors, agents,
                contractors and assigns shall not be liable to you for: (i)
                unauthorized or unlawful use of the Coin; (ii) the delay or
                error occurred during the use of the Coin due to the events
                outside our control (including force majeure events); and (iii)
                any loss, defect, or damage not resulting from our fault.
              </ListItem>
            </List>
            <SectionTitle>7. Our Intellectual Property Rights</SectionTitle>
            <Paragraph>
              We are the owner or the licensee of all rights in and to the Pro
              Hub, including all intellectual property rights and all technical
              infrastructure relating to it (including any updates and
              customizations). For the avoidance of doubt, the intellectual
              property rights include but not limited to trademarks, trade
              names, copyrights, goodwill, designs, patents, logos, symbols,
              slogans, know-how, trade secrets, codes, software, technology, and
              programs. All such rights are reserved by us. No intellectual
              property rights in or related to the Pro Hub are assigned or
              licensed to you.
            </Paragraph>
            <Paragraph>
              You will not use, copy, adapt, modify, prepare derivative works
              based upon, distribute, license, sell, transfer, publicly display,
              publicly perform, transmit, stream, broadcast or otherwise exploit
              the Pro Hub and all intellectual property rights related thereto
              for any purpose except as expressly permitted in the Terms. No
              licenses or rights are granted to you by implication or otherwise
              under any intellectual property rights owned or controlled by us,
              except for the licenses and rights expressly granted in the Terms.
            </Paragraph>
            <SectionTitle>8. Indemnity</SectionTitle>
            <Paragraph>
              You agree to indemnify and hold harmless us, our affiliates,
              officers, employees, directors, agents, contractors and assigns
              against any and all claims, liabilities, costs, and expenses,
              including, but not limited to, attorneys’ fees and expenses,
              arising out of a breach by you or any user of your account of the
              Terms or arising out of a breach of your obligations,
              representation and warranties under the Terms.
            </Paragraph>
            <SectionTitle>9. Limitation of Liability</SectionTitle>
            <Paragraph>
              The Pro Hub is provided on "as is" and "as available" basis. We do
              not represent or warrant that the Pro Hub or any content therein
              will meet your requirements, always be available or uninterrupted,
              accurate, complete or up-to-date.
            </Paragraph>
            <Paragraph>
              Nothing in the Terms shall exclude or limit our liability for
              losses which may not be lawfully excluded or limited by applicable
              law. Notwithstanding the foregoing, and except for any loss or
              damage is incurred by you as a result of our willful misconduct or
              negligence, we, our affiliates, officers, employees, directors,
              agents, contractors and assigns shall not be liable to you for:
            </Paragraph>
            <List>
              <ListItem>
                (i) any loss of profit, whether incurred directly or indirectly,
                any loss of goodwill, any loss of opportunity, any loss of
                anticipated savings, any loss of data suffered by you, or any
                indirect or consequential losses which may be incurred by you;
              </ListItem>
              <ListItem>
                (ii) any loss or damage which may be incurred by you as a result
                of any reliance placed by you on the completeness, accuracy or
                existence of any advertising, or as a result of any relationship
                or transaction between you and any advertiser or sponsor whose
                advertising appears on the Pro Hub; and
              </ListItem>
              <ListItem>
                (iii) any loss or damage which may be incurred by you as a
                result of your failure to provide us with accurate account
                information or your failure to keep your password or account
                details secure and confidential.
              </ListItem>
            </List>
            <Paragraph>
              We will not be liable for any loss or damage caused by a virus or
              other technologically harmful material that may infect your
              device, data or other proprietary material due to your use of the
              Pro Hub or to your downloading of any content on it, or on any
              website linked to it.
            </Paragraph>
            <Paragraph>
              The Pro Hub may contain links to other platforms, websites or
              content that are owned or controlled by third parties. We have no
              control and assume no responsibility for such third party
              platforms, websites, or content linked on the Pro Hub. Those links
              are not endorsed by us. We will not be liable for any loss or
              damage that may arise from your use of them. By using the Pro Hub,
              you agree that your access to or use of such linked platforms,
              websites or content is entirely at your own risk.
            </Paragraph>
            <SectionTitle>10. Links to External Websites</SectionTitle>
            <Paragraph>
              Links may be available on the Pro Hub to connect with external
              websites, which are administered by third parties and may not be
              related to us, and in which we have no involvement, nor has it
              verified their accuracy. we holds no liability for any damage that
              may arise from the content on, or defaults on agreements with, the
              external websites that are connected with the Pro Hub. Therefore,
              it is your responsibility to read the terms and conditions of
              those websites before you use those services. If you find an
              illegal or inappropriate content on the Pro Hub, please notify to
              us via the address, phone number, and email provided, for
              suspending or removing such content from the Pro Hub accordingly.
            </Paragraph>
            <SectionTitle>11. Miscellaneous</SectionTitle>
            <Paragraph>
              Security. We do not guarantee that the Pro Hub will be secure or
              free from bugs or viruses. You are responsible for configuring
              your information technology, computer programmes and platform to
              access the Pro Hub. You should use your own virus protection
              software.
            </Paragraph>
            <Paragraph>
              Assignment. We are entitled to assign our rights and obligations
              under the Terms to any third party at our sole discretion, and you
              agree to consent and provide all necessary assistance and support,
              including all necessary documents processed upon our request. You
              may not assign your rights or obligations under the Terms to any
              party without our prior written consent.
            </Paragraph>
            <Paragraph>
              No partnership. Nothing in the Terms shall be deemed to constitute
              either party a partner, joint venture agent or legal
              representative of the other party, or to create any fiduciary,
              employer-employee relationship between you and us.
            </Paragraph>
            <Paragraph>
              Force Majeure. We shall not be liable for non-performance, error,
              interruption or delay in the performance of our obligations under
              the Terms if this is due, in whole or in part, directly or
              indirectly, to an event or failure which is beyond our reasonable
              control, including but not limited to natural disaster, the novel
              Coronavirus or COVID-19, epidemics, mutated epidemics, pandemics,
              quarantine, and activities in accordance with any order of any
              government agency.
            </Paragraph>
            <Paragraph>
              No Waiver. Our failure to insist upon or enforce any provision of
              the Terms shall not be construed as a waiver of any provision or
              right.
            </Paragraph>
            <Paragraph>
              Severability. In the event that any one or more of the provisions
              contained in the Terms is invalid or unenforceable, such
              invalidity or unenforceability shall not affect the other
              provisions of the Terms. Governing law. The Terms are governed by
              and construed in accordance with the laws of Thailand. Choice of
              Forum. Any judicial proceeding as a result of a dispute or
              controversy arising out of or related to the Terms shall be
              commenced in courts located within Thailand.
            </Paragraph>
            <SectionTitle>12. Contact Us</SectionTitle>
            <Paragraph>
              If you have any comments or questions concerning the Terms, please
              do not hesitate to contact us at: The Up Rank Company Limited
            </Paragraph>
            <Paragraph>
              Address : 333/4 Ratchadaniwet Village Soi. 19 Pracha Uthit Rd. Sam
              Sen Nok, Huai Khwang, Bangkok 10310
            </Paragraph>
            <Paragraph>Line: @theuprank</Paragraph>
            <Paragraph>Email : kogclub.info@kogclub.com</Paragraph>
          </Container>
        </StyledContainer>
      </StyledWrapper>
    </MainLayout>
  );
};

export default Terms;
